import { Injectable } from '@angular/core';
import {
  Filters,
  SortBy,
} from 'src/app/components/lista-tabellare/classes/lista-tabellare-data-source';
import { LicenzeWebService } from './licenze-web.service';

export interface PromotoreLicenze {
  codice: string;
  descrizione: string;
  id: string;
}
interface LicenzaContesti {
  bilancio: number;
  sintesi: number;
  materialita: number;
  piano: number;
  policy: number;
  assessment: number;
  azienda: number;
}
interface Utilizzo {
  tipo: string;
  dataUtilizzo: string;
  idAzienda: string;
  idUtenteUtilizzatore: string;
  idRisorsa: string;
}

interface Pacchetto {
  codicePacchetto: string;
  idUtenteAcquisto: string;
  acquisti: LicenzaContesti;
  contatori: LicenzaContesti;
  utilizzi: Utilizzo[];
}

export interface PacchettoUtenza {
  codicePacchetto: string;
  dataAttivazione: string;
  dataScadenza: string;
  idUtenteAttivatore: string;
  acquisti: LicenzaContesti;
  contatori: LicenzaContesti;
  utilizzi: Utilizzo[];
  integrazioneIA: boolean;
  rinnovoAutomatico: boolean;
}

interface PacchettiAcquisti {
  dataAttivazione: string;
  dataScadenza: string;
  pacchetti: Pacchetto[];
}

export interface PacchettoAggiuntivoAttivazione {
  codicePacchetto: string;
  dataAttivazione: string;
  dataScadenza: string;
  acquisti: { [tipo: string]: number };
}

/**
 * Riga della lista attivazioni (proiezione del ws /licenze/attivazioni/list)
 */
export interface Attivazione {
  cognome: string;
  nome: string;
  emailAttivazione: string;
  idUtente: string;
  keyId: string;
  ruolo: string;
  codiceLicenza: string;
  codicePromotore: string;
  ragioneSocialeAziende: string[];
  codicePacchetto: string;
  maxNrAziende: number;
  acquisti: { [tipo: string]: number };
  tipoRinnovo: 'AUTOMATICO' | 'MANUALE' | null;
  dataPrimaAttivazione: string;
  dataAttivazione: string;
  dataScadenza: string;
  /**
   * false quando nel periodo selezionato sono stati acquistati solo pacchetti aggiuntivi:
   * la riga resta agganciata al pacchetto utenza che li copre, con date fuori periodo.
   */
  utenzaNelPeriodo: boolean;
  pacchettiAggiuntivi: PacchettoAggiuntivoAttivazione[];
}

/**
 * Parametri di selezione della dialog attivazioni
 */
export interface ParametriAttivazioni {
  modalitaPeriodo: 'anno' | 'intervallo';
  anno?: number | null;
  dataDa?: Date | null;
  dataA?: Date | null;
  codicePromotore?: string | null;
  ruolo?: string | null;
  tipoRinnovo?: 'AUTOMATICO' | 'MANUALE' | null;
}

export interface Licenza {
  tipo: 'UTENZA' | 'AGGIUNTIVO';
  codiceLicenza: string;
  idUtente: string;
  codicePromotore: string;
  freeze: boolean;
  pacchettoUtenza: PacchettoUtenza;
  pacchettiAcquisti: PacchettiAcquisti;
}

@Injectable({
  providedIn: 'root',
})
export class LicenzeService {
  private codiceLingua = 'it-IT';
  constructor(public licenzeWebService: LicenzeWebService) {}

  public getLicenzeByUtente(idUtente: string, codiceLicenza: string) {
    return this.licenzeWebService.getLicenzeByUtente(idUtente, codiceLicenza);
  }

  /* public putLicenzeByUtente(idUtente: string, codiceLicenza: string, licenza: any) {
    return this.licenzeWebService.putLicenzeByUtente(idUtente, codiceLicenza, licenza);
  }
 */
  public postAbilitaLicenza(idUtente: string, licenza: any) {
    return this.licenzeWebService.postAbilitaLicenza(idUtente, licenza);
  }

  public putLicenzaBase(
    idUtente: string,
    codiceLicenza: string,
    codicePacchetto: string,
    licenza: any,
  ) {
    return this.licenzeWebService.putLicenzaBase(idUtente, codiceLicenza, codicePacchetto, licenza);
  }

  public deleteLicenzaBase(idUtente: string, codiceLicenza: string, codicePacchetto: string) {
    return this.licenzeWebService.deleteLicenzaBase(idUtente, codiceLicenza, codicePacchetto);
  }

  public postLicenzeList() {
    return this.licenzeWebService.postLicenzeList();
  }

  public postLicenzeCreate() {
    return this.licenzeWebService.postLicenzeCreate();
  }

  public getPromotoriList() {
    return this.licenzeWebService.getPromotoriList();
  }
  public postPromotori() {
    return this.licenzeWebService.postPromotori();
  }

  public getUserHasLicense(nomeModulo: string) {
    return this.licenzeWebService.getUserHasLicense(nomeModulo);
  }
  public getUserHasLicenseIdUtente(idUtente: string, nomeModulo: string) {
    return this.licenzeWebService.getUserHasLicenseIdUtente(idUtente, nomeModulo);
  }

  public getPacchettiBase(
    page: number,
    pageSize: number,
    ricerca?: string,
    filters?: Filters[],
    sortBy?: SortBy[],
    codiceLicenza?: string,
    idUtente?: string,
  ) {
    return this.licenzeWebService.getPacchettiBase(
      page,
      pageSize,
      ricerca,
      filters,
      sortBy,
      codiceLicenza,
      idUtente,
    );
  }
  public getPacchettiAggiuntivi(
    page: number,
    pageSize: number,
    ricerca?: string,
    filters?: Filters[],
    sortBy?: SortBy[],
    codiceLicenza?: string,
    idUtente?: string,
  ) {
    return this.licenzeWebService.getPacchettiAggiuntivi(
      page,
      pageSize,
      ricerca,
      filters,
      sortBy,
      codiceLicenza,
      idUtente,
    );
  }

  public rinnovoLicenzaBase(codiceLicenza: string, idUtente: string, licenza: any) {
    return this.licenzeWebService.rinnovoLicenzaBase(codiceLicenza, idUtente, licenza);
  }

  public aggiungiPacchettoAggiuntivo(codiceLicenza: string, idUtente: string, licenza: any) {
    return this.licenzeWebService.aggiungiPacchettoAggiuntivo(codiceLicenza, idUtente, licenza);
  }
  public aggiungiEstensioneLicenzaBase(codiceLicenza: string, idUtente: string, licenza: any) {
    return this.licenzeWebService.aggiungiEstensioneLicenzaBase(codiceLicenza, idUtente, licenza);
  }

  public getLicenzaUtenteIncaricato(idAzienda: string) {
    return this.licenzeWebService.getLicenzaUtenteIncaricato(idAzienda);
  }

  public getAttivazioni(
    page: number,
    pageSize: number,
    ricerca?: string,
    filters?: Filters[],
    sortBy?: SortBy[],
  ) {
    return this.licenzeWebService.getAttivazioni(page, pageSize, ricerca, filters, sortBy);
  }

  public exportAttivazioni(ricerca?: string, filters?: Filters[], sortBy?: SortBy[]) {
    return this.licenzeWebService.exportAttivazioni(ricerca, filters, sortBy);
  }

  /**
   * Traduce i parametri della dialog attivazioni nei filters dei QueryParams attesi dal backend.
   *
   * Il periodo è sempre espresso come intervallo semiaperto [da, a) sulla data di attivazione
   * dei pacchetti: il ws attivazioni converte i due valori in date e applica gte sul primo e
   * lt sul secondo, quindi la data finale scelta dall'utente va passata già incrementata di un
   * giorno (vedi calcolaPeriodoAttivazioni).
   */
  public costruisciFiltriAttivazioni(parametri: ParametriAttivazioni): Filters[] {
    const filters: Filters[] = [];

    const { da, a } = this.calcolaPeriodoAttivazioni(parametri);

    filters.push({
      chiave: 'dataAttivazione',
      operator: 'betDate',
      values: [da.toISOString(), a.toISOString()],
    });

    if (parametri.codicePromotore) {
      filters.push({
        chiave: 'codicePromotore',
        operator: 'eq',
        values: [parametri.codicePromotore],
      });
    }

    if (parametri.ruolo) {
      filters.push({
        chiave: 'ruolo.roleName',
        operator: 'eq',
        values: [parametri.ruolo],
      });
    }

    if (parametri.tipoRinnovo) {
      // il backend legge i values come stringhe: il booleano va serializzato esplicitamente
      filters.push({
        chiave: 'rinnovoAutomatico',
        operator: 'is',
        values: [String(parametri.tipoRinnovo === 'AUTOMATICO')],
      });
    }

    return filters;
  }

  /**
   * Calcola l'intervallo [da, a) del periodo richiesto.
   * - modalità 'anno': dal 1 gennaio dell'anno al 1 gennaio dell'anno successivo
   * - modalità 'intervallo': dalla data da (inizio giornata) al giorno dopo la data a (esclusivo)
   */
  private calcolaPeriodoAttivazioni(parametri: ParametriAttivazioni): { da: Date; a: Date } {
    if (parametri.modalitaPeriodo === 'intervallo') {
      if (!parametri.dataDa || !parametri.dataA) {
        throw new Error('Periodo non valorizzato');
      }

      const da = new Date(parametri.dataDa);
      da.setHours(0, 0, 0, 0);

      // La data a è inclusiva per l'utente, quindi il limite superiore esclusivo è il giorno dopo
      const a = new Date(parametri.dataA);
      a.setHours(0, 0, 0, 0);
      a.setDate(a.getDate() + 1);

      return { da, a };
    }

    if (!parametri.anno) {
      throw new Error('Anno non valorizzato');
    }

    return {
      da: new Date(parametri.anno, 0, 1, 0, 0, 0, 0),
      a: new Date(parametri.anno + 1, 0, 1, 0, 0, 0, 0),
    };
  }
}
